import { useState, useEffect, useCallback } from 'react';
import {
  getRationaleOptions,
  addRationaleOption,
  updateRationaleOption,
  deleteRationaleOption
} from '../services/riskClassificationRationaleService';

/**
 * CRUD for risk assessment rationale options (nomenclature pool).
 * @param {string} organizationId
 * @param {string} [userId] - required for mutations
 */
export function useRiskRationaleOptions(organizationId, userId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await getRationaleOptions(organizationId);
      setItems(list);
    } catch (err) {
      console.error('[useRiskRationaleOptions]', err);
      setError(err.message || 'Failed to load rationale options');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = useCallback(
    async (label) => {
      if (!organizationId || !userId) throw new Error('organizationId and userId required');
      await addRationaleOption(organizationId, label, userId);
      await load();
    },
    [organizationId, userId, load]
  );

  const update = useCallback(
    async (optionId, label) => {
      if (!organizationId || !userId) throw new Error('organizationId and userId required');
      await updateRationaleOption(organizationId, optionId, label, userId);
      await load();
    },
    [organizationId, userId, load]
  );

  const remove = useCallback(
    async (optionId) => {
      if (!organizationId || !userId) throw new Error('organizationId and userId required');
      await deleteRationaleOption(organizationId, optionId, userId);
      await load();
    },
    [organizationId, userId, load]
  );

  return { items, loading, error, refetch: load, add, update, remove };
}
